import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { configuracionAPI, uploadAPI, alertasAPI } from '../services/api';
import { SecretInput } from '../utils/adminShared';
import TipTapEditor from './TipTapEditor';
import { toPercentInput, percentToDecimal } from '../utils/helpers';

const FIRMA_SEPARATOR = '<!--FIRMA-->';

const ConfiguracionPanel = ({ onConfigUpdate }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({});
    const [tipoCambioInfo, setTipoCambioInfo] = useState(null);
    const [loadingTC, setLoadingTC] = useState(false);
    const [metodosPagoLocal, setMetodosPagoLocal] = useState('');
    const [firmaLocal, setFirmaLocal] = useState('');
    const [slackConfig, setSlackConfig] = useState({ webhook_url: '', activo: false, app_url: '' });
    const [loadingSlackTest, setLoadingSlackTest] = useState(false);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [configRes, slackRes] = await Promise.all([
                configuracionAPI.getAll(),
                alertasAPI.getSlack()
            ]);
            setConfig(configRes.data || {});
            const mpRaw = configRes.data?.metodos_pago || '';
            if (mpRaw.includes(FIRMA_SEPARATOR)) {
                const [mp, firma] = mpRaw.split(FIRMA_SEPARATOR);
                setMetodosPagoLocal(mp || '');
                setFirmaLocal(firma || '');
            } else {
                setMetodosPagoLocal(mpRaw);
                setFirmaLocal('');
            }
            if (slackRes.data) setSlackConfig(slackRes.data);
        } catch (err) {
            console.error('Error al cargar configuración:', err);
        } finally {
            setLoading(false);
        }
    };

    // Solo actualiza estado local (sin llamar API)
    const handleLocalChange = (clave, valor) => {
        setConfig(prev => ({ ...prev, [clave]: valor }));
    };

    // Guardar inmediato (solo para SecretInput y logo que lo requieren)
    const handleImmediateSave = async (clave, valor) => {
        try {
            await configuracionAPI.update(clave, valor);
            setConfig(prev => {
                const newConfig = { ...prev, [clave]: valor };
                if (onConfigUpdate) onConfigUpdate(newConfig);
                return newConfig;
            });
        } catch (err) {
            toast.error('Error al actualizar: ' + err.message);
        }
    };

    // Guardar TODA la configuracion
    const guardarTodo = async () => {
        setSaving(true);
        try {
            // Combinar metodos de pago + firma en un solo campo
            const metodosPagoCombined = firmaLocal.trim()
                ? `${metodosPagoLocal}${FIRMA_SEPARATOR}${firmaLocal}`
                : metodosPagoLocal;

            // Preparar datos de configuracion general
            const configToSave = {
                tipo_cambio: config.tipo_cambio || '3.70',
                igv_rate: config.igv_rate || '0.18',
                rentabilidad: config.rentabilidad || '0.68',
                rentabilidad_costo_fijo: config.rentabilidad_costo_fijo || '0.68',
                prefijo_cotizacion: config.prefijo_cotizacion || 'COT',
                ultimo_numero_cotizacion: config.ultimo_numero_cotizacion || '0',
                empresa_nombre: config.empresa_nombre || '',
                empresa_ruc: config.empresa_ruc || '',
                empresa_email: config.empresa_email || '',
                empresa_telefono: config.empresa_telefono || '',
                metodos_pago: metodosPagoCombined,
            };

            // Guardar config general (batch)
            await configuracionAPI.updateMultiple(configToSave);

            // Guardar config Slack (sin webhook, eso se guarda por SecretInput)
            await alertasAPI.updateSlack({
                activo: slackConfig.activo,
                app_url: slackConfig.app_url
            });

            // Actualizar estado y notificar
            const newConfig = { ...config, metodos_pago: metodosPagoCombined };
            setConfig(newConfig);
            if (onConfigUpdate) onConfigUpdate(newConfig);
            toast.success('Configuración guardada correctamente');
        } catch (err) {
            toast.error('Error al guardar: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const actualizarTipoCambioSunat = async () => {
        setLoadingTC(true);
        try {
            const response = await configuracionAPI.getTipoCambioSunat();
            setTipoCambioInfo(response.data);
            if (response.data.venta) {
                const val = response.data.venta.toString();
                await configuracionAPI.update('tipo_cambio', val);
                const newConfig = { ...config, tipo_cambio: val };
                setConfig(newConfig);
                if (onConfigUpdate) onConfigUpdate(newConfig);
            }
            toast.success(`Tipo de cambio actualizado: ${response.data.venta} (${response.data.fuente})`);
        } catch (err) {
            toast.error('Error al obtener tipo de cambio: ' + err.message);
        } finally {
            setLoadingTC(false);
        }
    };

    if (loading) {
        return <div className="loading">Cargando configuración...</div>;
    }

    return (
        <div className="card">
            <div className="card-header">
                <h2>Configuración</h2>
            </div>
            <div className="admin-content">
                <div className="adminGrid">
                    <div className="adminBlock">
                        <h3 className="h3">Tipo de Cambio</h3>

                        <div className="tc-box">
                            <label className="field">
                                <span className="label">Tipo de cambio (PEN por 1 USD)</span>
                                <input
                                    className="input"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={config.tipo_cambio || '3.70'}
                                    onChange={(e) => handleLocalChange('tipo_cambio', e.target.value)}
                                />
                            </label>

                            <button
                                className="btn neon"
                                type="button"
                                onClick={actualizarTipoCambioSunat}
                                disabled={loadingTC}
                            >
                                {loadingTC ? 'Consultando...' : 'Actualizar desde SUNAT'}
                            </button>

                            {(() => {
                                const info = tipoCambioInfo || (() => {
                                    try { return config.tipo_cambio_info ? JSON.parse(config.tipo_cambio_info) : null; } catch(e) { return null; }
                                })();
                                if (!info) return null;
                                return (
                                    <div className="tc-info">
                                        <small>
                                            {info.compra != null && `Compra: ${info.compra} | `}
                                            {info.venta != null && `Venta: ${info.venta} | `}
                                            {info.fecha && `Fecha: ${info.fecha} | `}
                                            {info.actualizado && `Actualizado: ${info.actualizado} | `}
                                            {info.fuente && `Fuente: ${info.fuente}`}
                                        </small>
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="divider" />

                        <h3 className="h3">IGV</h3>
                        <label className="field">
                            <span className="label">Tasa de IGV (%)</span>
                            <input
                                className="input"
                                type="number"
                                step="0.01"
                                min="0"
                                value={toPercentInput(config.igv_rate || '0.18')}
                                onChange={(e) => handleLocalChange('igv_rate', percentToDecimal(e.target.value).toString())}
                            />
                            <div className="hint">Por defecto: 18%</div>
                        </label>

                        <div className="divider" />

                        <h3 className="h3">Margen de Rentabilidad</h3>
                        <div className="form-row">
                            <label className="field">
                                <span className="label">Rentabilidad General (%)</span>
                                <input
                                    className="input"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={toPercentInput(config.rentabilidad || '0.68')}
                                    onChange={(e) => handleLocalChange('rentabilidad', percentToDecimal(e.target.value).toString())}
                                />
                                <div className="hint">Para servicios con personal</div>
                            </label>
                            <label className="field">
                                <span className="label">Rentabilidad Costo Fijo (%)</span>
                                <input
                                    className="input"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={toPercentInput(config.rentabilidad_costo_fijo || '0.68')}
                                    onChange={(e) => handleLocalChange('rentabilidad_costo_fijo', percentToDecimal(e.target.value).toString())}
                                />
                                <div className="hint">Para costos fijos</div>
                            </label>
                        </div>

                        <div className="divider" />

                        <h3 className="h3">Numeración de Cotizaciones</h3>
                        <div className="form-row">
                            <label className="field">
                                <span className="label">Prefijo</span>
                                <input
                                    className="input"
                                    type="text"
                                    value={config.prefijo_cotizacion || 'COT'}
                                    onChange={(e) => handleLocalChange('prefijo_cotizacion', e.target.value)}
                                />
                            </label>
                            <label className="field">
                                <span className="label">Ultimo numero</span>
                                <input
                                    className="input"
                                    type="number"
                                    min="0"
                                    value={config.ultimo_numero_cotizacion || '0'}
                                    onChange={(e) => handleLocalChange('ultimo_numero_cotizacion', e.target.value)}
                                />
                            </label>
                        </div>
                        <div className="hint">
                            Proximo numero: {config.prefijo_cotizacion || 'COT'}-{(parseInt(config.ultimo_numero_cotizacion || '0') + 1)}
                        </div>

                        <div className="divider" />

                        <h3 className="h3">Integración con Google Sheets</h3>
                        <div className="field">
                            <span className="label">URL del Web App</span>
                            <SecretInput
                                hasValue={!!config.google_sheets_url}
                                onSave={(val) => handleImmediateSave('google_sheets_url', val)}
                                onClear={() => handleImmediateSave('google_sheets_url', '')}
                                placeholder="https://script.google.com/macros/s/..."
                            />
                            <div className="hint">
                                URL del Google Apps Script para sincronizar cotizaciones automáticamente.
                                Deja vacío para desactivar la sincronización.
                            </div>
                        </div>

                        <div className="divider" />

                        <h3 className="h3">Integración con Slack</h3>
                        <div className="field">
                            <span className="label">Webhook URL</span>
                            <SecretInput
                                hasValue={!!slackConfig.webhook_url}
                                onSave={async (val) => {
                                    try {
                                        await alertasAPI.updateSlack({ webhook_url: val });
                                        setSlackConfig(prev => ({ ...prev, webhook_url: '********' }));
                                        toast.success('Webhook de Slack guardado');
                                    } catch (err) {
                                        toast.error('Error: ' + err.message);
                                    }
                                }}
                                onClear={async () => {
                                    try {
                                        await alertasAPI.updateSlack({ webhook_url: '', activo: false });
                                        setSlackConfig({ webhook_url: '', activo: false, app_url: slackConfig.app_url });
                                        toast.success('Webhook de Slack eliminado');
                                    } catch (err) {
                                        toast.error('Error: ' + err.message);
                                    }
                                }}
                                placeholder="https://hooks.slack.com/services/T.../B.../xxx"
                            />
                            <div className="hint">
                                Crea un Incoming Webhook en Slack y pega la URL aquí.
                            </div>
                        </div>

                        <div className="form-row" style={{ alignItems: 'center', gap: '1rem' }}>
                            <label className="field" style={{ flex: 1 }}>
                                <span className="label">URL de la aplicación (para enlaces en Slack)</span>
                                <input
                                    className="input"
                                    type="text"
                                    value={slackConfig.app_url || ''}
                                    onChange={(e) => setSlackConfig(prev => ({ ...prev, app_url: e.target.value }))}
                                    placeholder="https://tudominio.com"
                                />
                                <div className="hint">URL pública de Don Presupuesto para generar enlaces en las alertas.</div>
                            </label>
                        </div>

                        <div className="form-row" style={{ alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={slackConfig.activo}
                                    onChange={(e) => setSlackConfig(prev => ({ ...prev, activo: e.target.checked }))}
                                />
                                <span>Activar notificaciones por Slack</span>
                            </label>

                            <button
                                className="btn ghost small"
                                type="button"
                                style={{ color: '#22c55e', borderColor: '#22c55e' }}
                                disabled={loadingSlackTest || !slackConfig.webhook_url}
                                onClick={async () => {
                                    setLoadingSlackTest(true);
                                    try {
                                        await alertasAPI.testSlack();
                                        toast.success('Mensaje de prueba enviado a Slack');
                                    } catch (err) {
                                        toast.error('Error: ' + err.message);
                                    } finally {
                                        setLoadingSlackTest(false);
                                    }
                                }}
                            >
                                {loadingSlackTest ? 'Enviando...' : 'Probar conexión'}
                            </button>
                        </div>
                    </div>

                    <div className="adminBlock">
                        <h3 className="h3">Datos de Empresa</h3>

                        <div className="field">
                            <span className="label">Logo de la empresa</span>
                            <div className="logo-upload-container">
                                {config.empresa_logo ? (
                                    <div className="logo-preview">
                                        <img
                                            src={config.empresa_logo.startsWith('data:') || config.empresa_logo.startsWith('http')
                                                ? config.empresa_logo
                                                : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${config.empresa_logo}`
                                            }
                                            alt="Logo"
                                        />
                                        <button
                                            type="button"
                                            className="btn ghost small danger"
                                            onClick={async () => {
                                                try {
                                                    const logoAnterior = config.empresa_logo;
                                                    if (logoAnterior && !logoAnterior.startsWith('data:') && !logoAnterior.startsWith('http')) {
                                                        const filename = logoAnterior.split('/').pop();
                                                        await uploadAPI.deleteFile(filename);
                                                    }
                                                    await handleImmediateSave('empresa_logo', '');
                                                    toast.success('Logo eliminado');
                                                } catch (err) {
                                                    toast.error('Error al eliminar: ' + err.message);
                                                }
                                            }}
                                        >
                                            Eliminar logo
                                        </button>
                                    </div>
                                ) : (
                                    <label className="logo-upload-btn">
                                        <input
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.svg,.ico,.webp"
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                if (file.size > 10 * 1024 * 1024) {
                                                    toast.error('El archivo es demasiado grande. Máximo 10MB', { id: 'upload-logo' });
                                                    e.target.value = '';
                                                    return;
                                                }
                                                const allowedExts = ['.jpg', '.jpeg', '.png', '.svg', '.ico', '.webp'];
                                                const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
                                                if (!allowedExts.includes(ext)) {
                                                    toast.error('Formato no permitido. Solo: JPG, PNG, SVG, ICO, WEBP', { id: 'upload-logo' });
                                                    e.target.value = '';
                                                    return;
                                                }
                                                try {
                                                    toast.loading('Subiendo logo...', { id: 'upload-logo' });
                                                    // Si habia logo previo guardado en BD, eliminarlo del disco
                                                    const logoAnterior = config.empresa_logo;
                                                    if (logoAnterior && !logoAnterior.startsWith('data:') && !logoAnterior.startsWith('http')) {
                                                        const filename = logoAnterior.split('/').pop();
                                                        try {
                                                            await uploadAPI.deleteFile(filename);
                                                        } catch (delErr) {
                                                            console.warn('No se pudo eliminar el logo anterior:', delErr);
                                                        }
                                                    }
                                                    const result = await uploadAPI.uploadLogo(file);
                                                    await handleImmediateSave('empresa_logo', result.url);
                                                    toast.success('Logo subido correctamente', { id: 'upload-logo' });
                                                } catch (err) {
                                                    const errorMsg = err.message || '';
                                                    if (errorMsg.includes('FORMAT') || errorMsg.includes('formato')) {
                                                        toast.error('Formato no permitido. Solo: JPG, PNG, SVG, ICO, WEBP', { id: 'upload-logo' });
                                                    } else if (errorMsg.includes('SIZE') || errorMsg.includes('grande') || errorMsg.includes('10MB')) {
                                                        toast.error('El archivo es demasiado grande. Máximo 10MB', { id: 'upload-logo' });
                                                    } else {
                                                        toast.error('Error al subir el logo: ' + errorMsg, { id: 'upload-logo' });
                                                    }
                                                }
                                                e.target.value = '';
                                            }}
                                            style={{ display: 'none' }}
                                        />
                                        <span className="btn ghost">Subir logo</span>
                                    </label>
                                )}
                            </div>
                            <div className="hint">Formatos: JPG, PNG, SVG, ICO, WEBP. Máximo 10MB. Si no subes un logo, se usará el default del sistema.</div>
                        </div>

                        <label className="field">
                            <span className="label">Nombre</span>
                            <input
                                className="input"
                                type="text"
                                value={config.empresa_nombre || ''}
                                onChange={(e) => handleLocalChange('empresa_nombre', e.target.value)}
                            />
                        </label>

                        <label className="field">
                            <span className="label">RUC</span>
                            <input
                                className="input"
                                type="text"
                                value={config.empresa_ruc || ''}
                                onChange={(e) => handleLocalChange('empresa_ruc', e.target.value)}
                            />
                        </label>

                        <label className="field">
                            <span className="label">Email</span>
                            <input
                                className="input"
                                type="email"
                                value={config.empresa_email || ''}
                                onChange={(e) => handleLocalChange('empresa_email', e.target.value)}
                            />
                        </label>

                        <label className="field">
                            <span className="label">Telefono</span>
                            <input
                                className="input"
                                type="text"
                                value={config.empresa_telefono || ''}
                                onChange={(e) => handleLocalChange('empresa_telefono', e.target.value)}
                            />
                        </label>

                        <div className="divider" />

                        <h3 className="h3">Métodos de Pago</h3>
                        <div className="field">
                            <span className="label">Información de cuentas bancarias</span>
                            <TipTapEditor
                                value={metodosPagoLocal}
                                onChange={(html) => setMetodosPagoLocal(html)}
                            />
                            <div className="hint">Datos bancarios que aparecerán en el PDF de cotización</div>
                        </div>

                        <div className="divider" />

                        <h3 className="h3">Firma</h3>
                        <div className="field">
                            <span className="label">Despedida y firma del responsable</span>
                            <TipTapEditor
                                value={firmaLocal}
                                onChange={(html) => setFirmaLocal(html)}
                            />
                            <div className="hint">Texto de cierre que aparecerá al final del PDF</div>
                        </div>

                        <div className="divider" />

                        <button
                            className="btn neon"
                            type="button"
                            onClick={guardarTodo}
                            disabled={saving}
                            style={{ width: '100%', padding: '12px', fontSize: '15px' }}
                        >
                            {saving ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfiguracionPanel;
